import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, ShoppingCart, Truck, MessagesSquare, Menu } from 'lucide-react';

import { Sidebar, UnreadBadge } from './Sidebar';
import { useChatStore } from '@/features/chat/store';
import { connectRealtime } from '@/features/chat/socket';
import { syncPush } from '@/features/notifications/push';
import logoMark from '@/images/optoviklogo.png';

const TABS = [
  { to: '/', label: 'Главная', icon: LayoutDashboard },
  { to: '/sales', label: 'Продажи', icon: ShoppingCart },
  { to: '/couriers', label: 'Курьеры', icon: Truck },
  { to: '/chat', label: 'Чат', icon: MessagesSquare },
];

export function Layout({ children }: { children: ReactNode }) {
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  const unread = useChatStore((s) => s.unread);

  // Один сокет на кабинет: счётчик чата, лента и статусы курьеров слушают его.
  useEffect(() => {
    connectRealtime();
    void syncPush();
  }, []);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!navOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setNavOpen(false);
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [navOpen]);

  const isChat = location.pathname.startsWith('/chat');

  return (
    <div className="h-full flex flex-col lg:flex-row">
      <header className="lg:hidden flex items-center gap-2.5 px-4 h-12 bg-white/95 backdrop-blur border-b border-line sticky top-0 z-30 shrink-0">
        <img src={logoMark} alt="" className="w-6 h-6 rounded-md object-cover shrink-0" />
        <div className="font-semibold text-[15px] text-ink tracking-tight truncate">Resstro Оптовик</div>
      </header>

      <div
        className={[
          'fixed inset-y-0 left-0 z-50 w-[84%] max-w-[300px] shadow-pop transform transition-transform duration-200 ease-out',
          'lg:static lg:translate-x-0 lg:w-60 lg:shadow-none lg:max-w-none lg:shrink-0',
          navOpen ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
      >
        <Sidebar onNavigate={() => setNavOpen(false)} />
      </div>

      {navOpen && (
        <button
          type="button"
          aria-label="Закрыть меню"
          onClick={() => setNavOpen(false)}
          className="fixed inset-0 z-40 bg-ink/40 lg:hidden"
        />
      )}

      <main className={`flex-1 min-w-0 bg-paper ${isChat ? 'overflow-hidden' : 'overflow-auto'}`}>
        <div className={isChat ? 'h-full' : 'px-4 pt-4 sm:px-6 sm:pt-6 pb-nav lg:pb-8 max-w-6xl mx-auto'}>{children}</div>
      </main>

      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-line"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        aria-label="Основные разделы"
      >
        <div className="grid grid-cols-5 h-16">
          {TABS.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) =>
                `relative flex flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors ${
                  isActive ? 'text-orange' : 'text-ink/50 active:text-ink'
                }`
              }
            >
              <span className="relative">
                <Icon size={22} strokeWidth={1.9} />
                {to === '/chat' && <UnreadBadge count={unread} className="absolute -top-1.5 -right-3" />}
              </span>
              {label}
            </NavLink>
          ))}
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            className="flex flex-col items-center justify-center gap-1 text-[11px] font-medium text-ink/50 active:text-ink"
          >
            <Menu size={22} strokeWidth={1.9} />
            Ещё
          </button>
        </div>
      </nav>
    </div>
  );
}
